import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./Gallery.css";
import galleryData from "../../data/galleryData";
import { Link } from "react-router-dom";
const Gallery = () => {
  /*==========================================
      STATES
  ==========================================*/

  const [activeCategory, setActiveCategory] = useState("All");

  const [selectedImage, setSelectedImage] = useState(null);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [imageLoaded, setImageLoaded] = useState(false);

  const touchStartX = useRef(0);

  const touchEndX = useRef(0);

  /*==========================================
      CATEGORY LIST
  ==========================================*/

  const categories = useMemo(() => {
    return ["All", ...new Set(galleryData.map((item) => item.category))];
  }, []);

  /*==========================================
      FILTER PROJECTS
  ==========================================*/

  const filteredProjects = useMemo(() => {
    if (activeCategory === "All") {
      return galleryData;
    }

    return galleryData.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  /*==========================================
      OPEN LIGHTBOX
  ==========================================*/

  const openLightbox = (project, index) => {
    setSelectedImage(project);

    setSelectedIndex(index);

    setImageLoaded(false);
  };

  /*==========================================
      CLOSE LIGHTBOX
  ==========================================*/

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);

    setImageLoaded(false);
  }, []);

  /*==========================================
      NEXT IMAGE
  ==========================================*/

  const nextImage = useCallback(() => {
    const nextIndex =
      selectedIndex === filteredProjects.length - 1 ? 0 : selectedIndex + 1;

    setSelectedIndex(nextIndex);

    setSelectedImage(filteredProjects[nextIndex]);

    setImageLoaded(false);
  }, [selectedIndex, filteredProjects]);

  /*==========================================
      PREVIOUS IMAGE
  ==========================================*/

  const prevImage = useCallback(() => {
    const prevIndex =
      selectedIndex === 0 ? filteredProjects.length - 1 : selectedIndex - 1;

    setSelectedIndex(prevIndex);

    setSelectedImage(filteredProjects[prevIndex]);

    setImageLoaded(false);
  }, [selectedIndex, filteredProjects]);
  /*==========================================
      KEYBOARD SUPPORT
  ==========================================*/
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      switch (e.key) {
        case "Escape":
          closeLightbox();
          break;

        case "ArrowRight":
          nextImage();
          break;

        case "ArrowLeft":
          prevImage();
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, nextImage, prevImage, closeLightbox]);
  /*==========================================
      BODY SCROLL LOCK
  ==========================================*/

  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedImage]);

  /*==========================================
      TOUCH EVENTS
  ==========================================*/

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;

    if (distance > 70) {
      nextImage();
    }

    if (distance < -70) {
      prevImage();
    }
  };

  /*==========================================
      JSX
  ==========================================*/

  return (
    <section className="gallery1">
      <div className="gallery1-container">
        {/*======================================
            SECTION HEADING
        ======================================*/}

        <div className="gallery1-heading">
          <span className="section-tag">PROJECT GALLERY</span>

          <h2>
            Explore Our Completed
            <br />
            Sports Infrastructure Projects
          </h2>

          <p>
            From basketball courts to synthetic turf fields, discover the
            quality, craftsmanship and precision behind every project delivered
            across India.
          </p>
        </div>

        {/*======================================
            FILTERS
        ======================================*/}

        <div className="gallery-filters">
          {categories.map((category) => (
            <button
              key={category}

              className={activeCategory === category ? "active" : ""}

              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/*======================================
            GALLERY GRID
        ======================================*/}

        <div className="gallery1-grid">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}

              className="gallery-card"

              onClick={() => openLightbox(project, index)}
            >
              <div className="gallery-image">
                <img
                  src={project.image}

                  alt={project.title}
                />
              </div>

              {/*==============================
                  HOVER OVERLAY
              ==============================*/}

              <div className="gallery-overlay">
                <span className="gallery-category">{project.category}</span>

                <h3>{project.title}</h3>

                <p>📍 {project.location}</p>

                <span className="gallery-surface">{project.surface}</span>

                <button className="gallery-view-btn">View Project</button>
              </div>
            </div>
          ))}
        </div>

        {/*======================================
            LOAD MORE
        ======================================*/}

        <div className="gallery-footer">
          <Link to="/gallery" className="gallery-load-btn">
            View More Projects Gallery
          </Link>
        </div>
      </div>

      {/*======================================
            PREMIUM LIGHTBOX
      ======================================*/}

      {selectedImage && (
        <div className="gallery-lightbox" onClick={closeLightbox}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}

            <button className="lightbox-close" onClick={closeLightbox}>
              ✕
            </button>

            {/* Previous */}

            <button className="lightbox-prev" onClick={prevImage}>
              ❮
            </button>

            {/* Image */}

            <img
              key={selectedIndex}
              src={selectedImage.image}
              alt={selectedImage.title}
              className={
                imageLoaded ? "lightbox-image loaded" : "lightbox-image"
              }
              onLoad={() => setImageLoaded(true)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />

            {/* Next */}

            <button className="lightbox-next" onClick={nextImage}>
              ❯
            </button>

            {/* Bottom Info */}

            <div className="lightbox-info">
              <div className="lightbox-top">
                <span className="lightbox-category">
                  {selectedImage.category}
                </span>

                <span className="lightbox-counter">
                  {selectedIndex + 1} / {filteredProjects.length}
                </span>
              </div>

              <h2>{selectedImage.title}</h2>

              <p>📍 {selectedImage.location}</p>

              <div className="lightbox-surface">🏟 {selectedImage.surface}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
